// src/api/platformAttendanceApi.ts
//
// Thin CRM BFF API layer for the Central Attendance capability (ADR-0017).
//
// Requests go same-origin to the CRM BFF at `/api/platform/attendance/*` via the
// shared `baseApi` (cookie/session auth, boot-gated). The BROWSER NEVER CALLS
// CENTRAL and holds no Central Bearer token — the server forwards the session's
// Central token and derives user + workspace from it. These wrappers pass no
// workspaceId/userId/timestamps and compute no derived state, no timers, and no
// polling — that is the header quick-action PR's concern.
//
// DTO types come from the published `@roi-pros/central-attendance` package
// (never re-declared).

import { baseApi } from './baseApi';
import type {
  CurrentSessionOk,
  AttendanceSessionOk,
  ClockInRequestBody,
  StartBreakRequestBody,
} from '@roi-pros/central-attendance';

export const platformAttendanceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET current-session → canonical CurrentSessionOk ({ ok, session|null }).
    getCurrentAttendanceSession: builder.query<CurrentSessionOk, void>({
      query: () => ({ url: '/platform/attendance/current-session', method: 'GET' }),
      providesTags: [{ type: 'Attendance', id: 'CURRENT' }],
    }),

    // POST clock-in — forwards only { workLocation }.
    attendanceClockIn: builder.mutation<AttendanceSessionOk, ClockInRequestBody>({
      query: (body) => ({ url: '/platform/attendance/clock-in', method: 'POST', body }),
      invalidatesTags: [{ type: 'Attendance', id: 'CURRENT' }],
    }),

    // POST clock-out — no body.
    attendanceClockOut: builder.mutation<AttendanceSessionOk, void>({
      query: () => ({ url: '/platform/attendance/clock-out', method: 'POST' }),
      invalidatesTags: [{ type: 'Attendance', id: 'CURRENT' }],
    }),

    // POST start-break — forwards only { kind }.
    attendanceStartBreak: builder.mutation<AttendanceSessionOk, StartBreakRequestBody>({
      query: (body) => ({ url: '/platform/attendance/start-break', method: 'POST', body }),
      invalidatesTags: [{ type: 'Attendance', id: 'CURRENT' }],
    }),

    // POST end-break — no body.
    attendanceEndBreak: builder.mutation<AttendanceSessionOk, void>({
      query: () => ({ url: '/platform/attendance/end-break', method: 'POST' }),
      invalidatesTags: [{ type: 'Attendance', id: 'CURRENT' }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCurrentAttendanceSessionQuery,
  useAttendanceClockInMutation,
  useAttendanceClockOutMutation,
  useAttendanceStartBreakMutation,
  useAttendanceEndBreakMutation,
} = platformAttendanceApi;
